import type { LabelCanvasElement } from './labelCanvasTypes';
import { newCanvasElementId } from './labelCanvasTypes';

import type { LabelCanvasWrapGuide } from './productLabelTypes';

export type LabelCanvasPreset = {
  id: string;
  name: string;
  description: string;
  widthIn: number;
  heightIn: number;
  backgroundColor: string;
  accentColor: string;
  textColor: string;
  wrapGuide?: LabelCanvasWrapGuide;
  elements: LabelCanvasElement[];
};

function el(partial: Omit<LabelCanvasElement, 'id'> & { id?: string }): LabelCanvasElement {
  return { id: partial.id || newCanvasElementId(), ...partial };
}

/** Orange juice style — bold left branding + bordered info panel */
export const FRESH_JUICE_CANVAS: LabelCanvasPreset = {
  id: 'fresh-juice',
  name: 'Fresh juice',
  description: 'Bold title left · info panel right',
  widthIn: 6,
  heightIn: 3,
  backgroundColor: '#f97316',
  accentColor: '#ffffff',
  textColor: '#ffffff',
  elements: [
    el({
      type: 'shape',
      x: 0,
      y: 0,
      w: 100,
      h: 100,
      zIndex: 0,
      backgroundColor: '#f97316',
      shape: 'rect',
    }),
    el({
      type: 'text',
      x: 6,
      y: 18,
      w: 48,
      h: 38,
      zIndex: 2,
      text: 'ORANGE\nFRESH JUICE',
      fontSizePct: 6.5,
      fontWeight: 800,
      color: '#ffffff',
      textAlign: 'left',
      lineHeight: 1.05,
      textTransform: 'uppercase',
    }),
    el({
      type: 'text',
      x: 6,
      y: 58,
      w: 40,
      h: 8,
      zIndex: 2,
      text: '100% Pure & Natural',
      fontSizePct: 2.4,
      fontWeight: 400,
      color: '#ffffff',
    }),
    el({
      type: 'panel',
      x: 58,
      y: 8,
      w: 36,
      h: 84,
      zIndex: 3,
      panelTitle: 'INGREDIENTS',
      text: 'Fresh orange juice, water, natural flavors.',
      borderWidth: 2,
      borderColor: '#ffffff',
      borderRadius: 6,
      color: '#ffffff',
      fontSizePct: 2,
    }),
    el({
      type: 'boundField',
      fieldKey: 'netQty',
      x: 60,
      y: 62,
      w: 32,
      h: 8,
      zIndex: 4,
      fontSizePct: 2.2,
      fontWeight: 700,
      color: '#ffffff',
      textTransform: 'uppercase',
    }),
    el({
      type: 'boundField',
      fieldKey: 'company',
      x: 60,
      y: 72,
      w: 32,
      h: 14,
      zIndex: 4,
      fontSizePct: 1.8,
      color: '#ffffff',
      lineHeight: 1.3,
    }),
    el({
      type: 'image',
      x: 2,
      y: -8,
      w: 22,
      h: 40,
      zIndex: 1,
      objectFit: 'contain',
    }),
  ],
};

/** Strawberry premium — bands + arch + columns */
export const PREMIUM_STRAWBERRY_CANVAS: LabelCanvasPreset = {
  id: 'premium-strawberry',
  name: 'Premium strawberry',
  description: 'Header band · arch title · nutrition panel',
  widthIn: 6,
  heightIn: 4,
  backgroundColor: '#ffffff',
  accentColor: '#9f1239',
  textColor: '#0f172a',
  elements: [
    el({
      type: 'shape',
      x: 0,
      y: 0,
      w: 100,
      h: 8,
      zIndex: 1,
      backgroundColor: '#9f1239',
      shape: 'rect',
    }),
    el({
      type: 'shape',
      x: 0,
      y: 92,
      w: 100,
      h: 8,
      zIndex: 1,
      backgroundColor: '#9f1239',
      shape: 'rect',
    }),
    el({
      type: 'shape',
      x: 28,
      y: 0,
      w: 44,
      h: 22,
      zIndex: 2,
      backgroundColor: '#9f1239',
      shape: 'semicircle-bottom',
    }),
    el({
      type: 'boundField',
      fieldKey: 'name',
      x: 30,
      y: 4,
      w: 40,
      h: 10,
      zIndex: 5,
      fontSizePct: 5,
      fontWeight: 700,
      color: '#ffffff',
      textAlign: 'center',
      fontStyle: 'italic',
    }),
    el({
      type: 'text',
      x: 30,
      y: 13,
      w: 40,
      h: 6,
      zIndex: 5,
      text: 'FRESH JUICE',
      fontSizePct: 2.8,
      fontWeight: 800,
      color: '#ffffff',
      textAlign: 'center',
      textTransform: 'uppercase',
    }),
    el({
      type: 'text',
      x: 30,
      y: 18,
      w: 40,
      h: 5,
      zIndex: 5,
      text: '100% NATURAL',
      fontSizePct: 2,
      color: '#ffffff',
      textAlign: 'center',
      textTransform: 'uppercase',
    }),
    el({
      type: 'boundField',
      fieldKey: 'description',
      x: 4,
      y: 28,
      w: 28,
      h: 28,
      zIndex: 3,
      fontSizePct: 2,
      color: '#0f172a',
      lineHeight: 1.35,
    }),
    el({
      type: 'boundField',
      fieldKey: 'benefits',
      x: 4,
      y: 58,
      w: 28,
      h: 12,
      zIndex: 3,
      fontSizePct: 2,
      fontWeight: 600,
      color: '#0f172a',
    }),
    el({
      type: 'boundField',
      fieldKey: 'expiry',
      x: 4,
      y: 72,
      w: 28,
      h: 8,
      zIndex: 3,
      fontSizePct: 2,
      color: '#0f172a',
    }),
    el({
      type: 'nutritionTable',
      x: 68,
      y: 28,
      w: 28,
      h: 38,
      zIndex: 4,
      panelTitle: 'Nutrition Facts',
      nutritionServingNote: 'Per 500 ml serving',
      borderWidth: 2,
      borderColor: '#0f172a',
      color: '#0f172a',
      fontSizePct: 1.8,
    }),
    el({
      type: 'barcode',
      x: 70,
      y: 70,
      w: 24,
      h: 14,
      zIndex: 4,
    }),
    el({
      type: 'boundField',
      fieldKey: 'image',
      x: 34,
      y: 52,
      w: 32,
      h: 38,
      zIndex: 3,
    }),
    el({
      type: 'boundField',
      fieldKey: 'netQty',
      x: 38,
      y: 93,
      w: 24,
      h: 6,
      zIndex: 6,
      fontSizePct: 3,
      fontWeight: 700,
      color: '#ffffff',
      textAlign: 'center',
      textTransform: 'uppercase',
    }),
  ],
};

/** Wide wrap label — three zones like a jar wrap */
export const WRAP_COOKIE_CANVAS: LabelCanvasPreset = {
  id: 'wrap-cookies',
  name: 'Wrap-around (3 panels)',
  description: 'Wide label · front center · sides for legal',
  widthIn: 8,
  heightIn: 2.5,
  backgroundColor: '#c45c26',
  accentColor: '#4a2c2a',
  textColor: '#4a2c2a',
  wrapGuide: '3-panel',
  elements: [
    el({
      type: 'shape',
      x: 0,
      y: 0,
      w: 100,
      h: 100,
      zIndex: 0,
      backgroundColor: '#c45c26',
      shape: 'rect',
    }),
    el({
      type: 'shape',
      x: 0,
      y: 35,
      w: 100,
      h: 30,
      zIndex: 1,
      backgroundColor: '#4a2c2a',
      shape: 'rect',
    }),
    el({
      type: 'shape',
      x: 36,
      y: 8,
      w: 28,
      h: 84,
      zIndex: 2,
      backgroundColor: '#faf6f0',
      borderRadius: 40,
      shape: 'rect',
    }),
    el({
      type: 'text',
      x: 38,
      y: 22,
      w: 24,
      h: 40,
      zIndex: 4,
      text: 'Choco\nCookies',
      fontSizePct: 7,
      fontWeight: 700,
      color: '#4a2c2a',
      textAlign: 'center',
      lineHeight: 1,
      fontStyle: 'italic',
    }),
    el({
      type: 'boundField',
      fieldKey: 'image',
      x: 40,
      y: 58,
      w: 20,
      h: 32,
      zIndex: 4,
    }),
    el({
      type: 'boundField',
      fieldKey: 'description',
      x: 4,
      y: 12,
      w: 28,
      h: 50,
      zIndex: 3,
      fontSizePct: 1.8,
      color: '#ffffff',
      lineHeight: 1.3,
    }),
    el({
      type: 'barcode',
      x: 8,
      y: 72,
      w: 20,
      h: 22,
      zIndex: 3,
    }),
    el({
      type: 'panel',
      x: 68,
      y: 10,
      w: 28,
      h: 55,
      zIndex: 3,
      panelTitle: 'Ingredients',
      text: 'Wheat flour, sugar, cocoa, butter, chocolate chips.',
      color: '#ffffff',
      fontSizePct: 1.7,
    }),
    el({
      type: 'text',
      x: 72,
      y: 68,
      w: 8,
      h: 28,
      zIndex: 3,
      text: 'Freshly Baked.\nAlways Loved.',
      fontSizePct: 2.2,
      fontWeight: 700,
      color: '#ffffff',
      rotation: 90,
    }),
  ],
};

export const LABEL_CANVAS_PRESETS: LabelCanvasPreset[] = [
  FRESH_JUICE_CANVAS,
  PREMIUM_STRAWBERRY_CANVAS,
  WRAP_COOKIE_CANVAS,
];

export function clonePresetElements(preset: LabelCanvasPreset): LabelCanvasElement[] {
  return preset.elements.map((e) => ({
    ...e,
    id: newCanvasElementId(),
  }));
}
